import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

interface ProgressStep {
  id: string;
  label: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  duration?: number; // estimated duration in seconds
}

interface ProgressIndicatorProps {
  steps: ProgressStep[];
  currentStep?: string;
  title?: string;
  show: boolean;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  steps,
  currentStep,
  title = 'Przetwarzanie...',
  show
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-6 text-center text-gray-800">{title}</h3>

        <div className="space-y-4">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`flex items-center p-3 rounded-lg transition-all ${
                step.status === 'in_progress'
                  ? 'bg-blue-50 border-2 border-blue-300'
                  : step.status === 'completed'
                  ? 'bg-green-50 border border-green-200'
                  : step.status === 'error'
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="mr-3">
                {step.status === 'in_progress' && (
                  <Loader2 className="text-blue-600 animate-spin" size={24} />
                )}
                {step.status === 'completed' && (
                  <CheckCircle2 className="text-green-600" size={24} />
                )}
                {step.status === 'error' && (
                  <AlertCircle className="text-red-600" size={24} />
                )}
                {step.status === 'pending' && (
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                )}
              </div>

              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span
                    className={`font-medium ${
                      step.status === 'in_progress'
                        ? 'text-blue-800'
                        : step.status === 'completed'
                        ? 'text-green-800'
                        : step.status === 'error'
                        ? 'text-red-800'
                        : 'text-gray-600'
                    }`}
                  >
                    {step.label}
                  </span>
                  {step.duration && step.status === 'in_progress' && (
                    <span className="text-xs text-gray-500">~{step.duration}s</span>
                  )}
                </div>

                {step.status === 'in_progress' && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div className="bg-blue-600 h-1.5 rounded-full animate-pulse" style={{ width: '60%' }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {steps.every(s => s.status === 'completed' || s.status === 'error') && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {steps.some(s => s.status === 'error')
                ? '⚠️ Wystąpił błąd podczas przetwarzania'
                : '✅ Operacja zakończona pomyślnie'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple loading overlay
interface LoadingOverlayProps {
  show: boolean;
  message: string;
  submessage?: string;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ show, message, submessage }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
        <Loader2 className="text-blue-600 animate-spin mx-auto mb-4" size={48} />
        <h3 className="text-xl font-bold mb-2 text-gray-800">{message}</h3>
        {submessage && <p className="text-sm text-gray-600">{submessage}</p>}
      </div>
    </div>
  );
};

// Progress bar component
interface ProgressBarProps {
  value: number; // 0-100
  max?: number;
  label?: string;
  showPercentage?: boolean;
  color?: 'blue' | 'green' | 'purple' | 'red';
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  label,
  showPercentage = true,
  color = 'blue'
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  const colorClasses = {
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    purple: 'bg-purple-600',
    red: 'bg-red-600'
  };

  return (
    <div className="w-full">
      {label && (
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          {showPercentage && (
            <span className="text-sm font-bold text-gray-900">{Math.round(percentage)}%</span>
          )}
        </div>
      )}
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div
          className={`${colorClasses[color]} h-2.5 rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Estimated time remaining
interface TimeRemainingProps {
  seconds: number;
}

export const TimeRemaining: React.FC<TimeRemainingProps> = ({ seconds }) => {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    if (remaining <= 0) return;

    const interval = setInterval(() => {
      setRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [remaining]);

  const minutes = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return (
    <div className="flex items-center text-sm text-gray-600">
      <Loader2 className="animate-spin mr-2" size={16} />
      <span>
        Pozostało: {minutes > 0 && `${minutes}m `}{secs}s
      </span>
    </div>
  );
};
